<template>
    <div data-test-view="goals-details" class="goals-details">
        <div v-if="!goal.isNew" class="row">
            <div class="col-md-8">
                <l-card-content>
                    <!-- Header -->
                    <div style="display: flex">
                        <!-- Icon -->
                        <div style="flex: 0 0 10%;" class="hidden-xs">
                            <l-profile-picture
                                v-if="goal.type === 'goalUser' && goal.user && goal.user.picture"
                                :user="goal.user"
                                size="m"
                            />
                            <img
                                v-if="goal.icon && !(goal.user && goal.user.picture)"
                                :src="goal.icon"
                                class="img-circle user-pic"
                                alt="Goal Icon"
                            />
                        </div>
                        <!--/Icon -->

                        <!-- Headline -->
                        <div style="flex: 0 0 90%">
                            <l-headline
                                :title="goal.name"
                                :title-popover="goal.name"
                                :tabs="tabs"
                            />
                        </div>
                        <!--/Headline -->
                    </div>

                    <l-confirmation-modal
                        v-if="freezeGoalCheck"
                        :text="$t('goals.update.helpFreezeWarning')"
                        @confirm="freezeGoal()"
                        @cancel="freezeGoalCheck = false"
                    />

                    <l-confirmation-modal
                        v-if="unfreezeGoalCheck"
                        :text="$t('goals.update.helpUnfreezeWarning')"
                        @confirm="unfreezeGoal()"
                        @cancel="unfreezeGoalCheck = false"
                    />

                    <l-confirmation-modal
                        v-if="removeGoalCheck"
                        :text="$t('goals.update.helpRemoveWarning')"
                        @confirm="removeGoal()"
                        @cancel="removeGoalCheck = false"
                    />

                    <!-- Error -->
                    <div v-if="error" class="panel panel-warning">
                        <div class="panel-heading">
                            <span>{{ error.message }}</span>
                        </div>
                    </div>
                    <!--/Error -->

                    <div>
                        <l-progress
                            :progress-percent="goal.progress || 0"
                            :progress-bar-class="
                                goal.progress <= 33
                                    ? 'progress-bar-danger'
                                    : goal.progress > 33 && goal.progress <= 66
                                    ? 'progress-bar-warning'
                                    : goal.progress > 66
                                    ? 'progress-bar-success'
                                    : ''
                            "
                            size="xs"
                        />

                        <div class="row">
                            <div
                                class="col-md-12"
                                style="display: flex;align-items: center;justify-content: space-between;"
                            >
                                <!-- Goal status -->
                                <div class="btn-group">
                                    <b-dropdown
                                        v-if="
                                            goal.access.canUpdateStatus ||
                                                (goal.toDiscuss && goal.toDiscuss !== 'zero')
                                        "
                                    >
                                        <template #button-content>
                                            <span
                                                class="btn btn-sm dropdown-toggle"
                                                :class="{
                                                    'btn-success':
                                                        goal.toDiscuss === 'done' ||
                                                        goal.toDiscuss === 'onTrack',
                                                    'btn-warning': goal.toDiscuss === 'inputNeeded',
                                                    'btn-danger':
                                                        goal.toDiscuss === 'offTrack' ||
                                                        goal.toDiscuss === 'canceled',
                                                }"
                                            >
                                                <span
                                                    v-if="
                                                        !goal.toDiscuss || goal.toDiscuss === 'zero'
                                                    "
                                                >
                                                    {{ $t(`goals.list.labelSelectStatus`) }}
                                                </span>
                                                <span v-else-if="goal.toDiscuss === 'done'">
                                                    <i class="fal fa-flag"></i>
                                                    <span class="l-default-margin-s">{{
                                                        $t(`goals.update.labelDone`)
                                                    }}</span>
                                                </span>
                                                <span v-else-if="goal.toDiscuss === 'onTrack'">
                                                    <i class="fal fa-check"></i>
                                                    <span class="l-default-margin-s">{{
                                                        $t(`goals.update.labelOnTrack`)
                                                    }}</span>
                                                </span>
                                                <span v-else-if="goal.toDiscuss === 'inputNeeded'">
                                                    <i class="fal fa-exclamation-triangle"></i>
                                                    <span class="l-default-margin-s">{{
                                                        $t(`goals.update.labelInputNeeded`)
                                                    }}</span>
                                                </span>
                                                <span v-else-if="goal.toDiscuss === 'offTrack'">
                                                    <i class="fal fa-times"></i>
                                                    <span class="l-default-margin-s">{{
                                                        $t(`goals.update.labelOffTrack`)
                                                    }}</span>
                                                </span>
                                                <span v-else-if="goal.toDiscuss === 'canceled'">
                                                    <i class="fal fa-stop-circle"></i>
                                                    <span class="l-default-margin-s">{{
                                                        $t(`goals.update.labelCanceled`)
                                                    }}</span>
                                                </span>
                                                <i class="fal fa-caret-down l-default-margin-s"></i>
                                            </span>
                                        </template>

                                        <b-dropdown-item
                                            v-if="goal.access.canUpdateStatus"
                                            class="clickable"
                                            data-test-class="goals-status-done"
                                            @click="updateGoalStatus('done')"
                                        >
                                            <i class="fal fa-flag"></i>
                                            <span>{{ $t("goals.update.labelDone") }}</span>
                                        </b-dropdown-item>

                                        <b-dropdown-item
                                            v-if="goal.access.canUpdateStatus"
                                            class="clickable"
                                            data-test-class="goals-status-on-track"
                                            @click="updateGoalStatus('onTrack')"
                                        >
                                            <i class="fal fa-check"></i>
                                            <span>{{ $t("goals.update.labelOnTrack") }}</span>
                                        </b-dropdown-item>

                                        <b-dropdown-item
                                            v-if="goal.access.canUpdateStatus"
                                            class="clickable"
                                            data-test-class="goals-status-input-needed"
                                            @click="updateGoalStatus('inputNeeded')"
                                        >
                                            <i class="fal fa-exclamation-triangle"></i>
                                            <span>{{ $t("goals.update.labelInputNeeded") }}</span>
                                        </b-dropdown-item>

                                        <b-dropdown-item
                                            v-if="goal.access.canUpdateStatus"
                                            class="clickable"
                                            data-test-class="goals-status-off-track"
                                            @click="updateGoalStatus('offTrack')"
                                        >
                                            <i class="fal fa-times"></i>
                                            <span>{{ $t("goals.update.labelOffTrack") }}</span>
                                        </b-dropdown-item>

                                        <b-dropdown-item
                                            v-if="goal.access.canUpdateStatus"
                                            class="clickable"
                                            data-test-class="goals-status-canceled"
                                            @click="updateGoalStatus('canceled')"
                                        >
                                            <i class="fal fa-stop-circle"></i>
                                            <span>{{ $t("goals.update.labelCanceled") }}</span>
                                        </b-dropdown-item>

                                        <b-dropdown-item
                                            v-if="goal.access.canUpdateStatus"
                                            v-show="goal.toDiscuss && goal.toDiscuss !== 'zero'"
                                            class="clickable"
                                            data-test-class="goals-status-zero"
                                            @click="updateGoalStatus('zero')"
                                        >
                                            <i class="fal fa-trash"></i>
                                            <span>{{ $t("goals.update.labelZero") }}</span>
                                        </b-dropdown-item>
                                    </b-dropdown>
                                </div>
                                <!--/Goal status -->

                                <span class="pull-right hidden-xs tw-my-2">
                                    <!-- Tags -->
                                    <span v-for="tag in filteredGoalTags" :key="tag._id">
                                        <span class="explanation l-default-margin-s">
                                            <i class="fal fa-tag r-default-margin-s"></i>
                                            {{ tag.name.slice(0, 200 / goal.tags.length) }}
                                            <span v-show="tag.name.length > 200 / goal.tags.length">
                                                ...
                                            </span>
                                        </span>
                                    </span>
                                    <!--/Tags -->

                                    <!-- Deadline -->
                                    <span
                                        v-if="goal.deadline"
                                        v-b-popover.hover.top="{
                                            content: $t(`goals.update.labelDeadline`),
                                        }"
                                        class="l-default-margin-s"
                                    >
                                        <i class="fal fa-calendar"></i>
                                        <span class="explanation l-default-margin-s">
                                            {{ goal.deadline | moment("MMM DD, YYYY") }}
                                        </span>
                                    </span>
                                    <!--/Deadline -->

                                    <!-- Visibility -->
                                    <span class="explanation l-default-margin-s">
                                        <i class="fal fa-eye r-default-margin-s"></i>
                                        <span v-if="goal.visibility === 'me'">
                                            {{ $t(`goals.update.optionVisibilityMe`) }}
                                        </span>
                                        <span
                                            v-else-if="
                                                (goal.type === 'goalOtherUser' ||
                                                    goal.type === 'goalDirectReport') &&
                                                    goal.visibility != 'all'
                                            "
                                        >
                                            {{ $t(`goals.update.optionVisibilityLimitedUser`) }}
                                        </span>
                                        <span
                                            v-else-if="
                                                goal.type === 'goalLocalTeam' &&
                                                    goal.visibility === 'limited'
                                            "
                                        >
                                            {{ $t(`goals.update.optionVisibilityLocalTeam`) }}
                                        </span>
                                        <span v-else-if="goal.visibility === 'limited'">
                                            {{ $t(`goals.update.optionVisibilityLimited`) }}
                                        </span>
                                        <span
                                            v-else-if="
                                                goal.type === 'goalTeam' ||
                                                    goal.visibility === 'all'
                                            "
                                        >
                                            {{ $t(`goals.update.optionVisibilityPublic`) }}
                                        </span>
                                    </span>
                                    <!--/Visibility -->
                                </span>
                            </div>
                        </div>

                        <l-space :size="1" />

                        <div v-show="goal.description">
                            <l-text :text="goal.description" />
                            <hr />
                        </div>

                        <!-- Key Results -->
                        <div v-show="!goal.keyResults.length" class="row">
                            <div class="col-xs-8">
                                <div class="explanation" style="margin-top: 10px">
                                    <span v-show="goal.metricWeight">
                                        <strong>{{ goal.metricWeight }}% - </strong>
                                    </span>
                                </div>
                                <div
                                    v-b-popover.hover.top="goal.name"
                                    style="font-size: 15px; margin-bottom: 10px"
                                    class="tw-line-clamp-1"
                                >
                                    <strong>{{ goal.name }}</strong>
                                </div>
                            </div>
                            <div class="col-xs-4 active">
                                <div class="form-group label-floating">
                                    <div class="input-group">
                                        <l-input-text
                                            v-model="goal.metricCurrent"
                                            type="number"
                                            :label="``"
                                            :disabled="
                                                goal.dynamicProgressEnabled ||
                                                    !goal.access.canUpdateStatus
                                            "
                                            hide-help-block
                                        />
                                        <span class="input-group-addon" style="font-size: 16px">
                                            / {{ goal.metricEnd }}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div v-for="keyResult in keyResults" :key="keyResult._id">
                            <l-goal-details-key-result
                                :goal="goal"
                                :key-result="keyResult"
                                @update="updateKR"
                            />
                            <l-space :size="1" />
                        </div>
                        <!--/Key results / Initiatives -->

                        <!-- KR edit modal -->
                        <l-goal-k-r-edit-modal
                            v-show="$route.query.editGoalId && $route.query.editKeyResultId"
                            :goal="goal"
                            @update="keyResultUpdated"
                        ></l-goal-k-r-edit-modal>
                        <!--/ KR edit modal -->

                        <br />

                        <!-- Interactions -->
                        <div class="material-tabs hidden-xs clickable">
                            <a
                                :class="{ active: showInteractions === 'feedback' }"
                                @click="showInteractions = 'feedback'"
                                @keyup.enter="showInteractions = 'feedback'"
                            >
                                <span>{{ $t(`goals.update.titleComment`) }}</span>
                                <span class="material-tabs-bar"></span>
                            </a>
                            <a
                                :class="{ active: showInteractions === 'history' }"
                                @click="showInteractions = 'history'"
                                @keyup.enter="showInteractions = 'history'"
                            >
                                <span>{{ $t(`goals.update.titleHistory`) }}</span>
                                <span class="material-tabs-bar"></span>
                            </a>
                        </div>

                        <!-- Comments -->
                        <div v-if="showInteractions === 'feedback'">
                            <l-rich-text-editor
                                v-model="comment"
                                :content="comment"
                                :placeholder="$t(`goals.update.labelComment`)"
                                :allow-mention="true"
                            ></l-rich-text-editor>

                            <div class="row">
                                <div class="col-xs-8">
                                    <div class="tw-mt-4 tw-text-gray-500 pull-right">
                                        {{ $t(`goals.update.labelMention`) }}
                                    </div>
                                </div>
                                <div class="col-xs-4">
                                    <button
                                        class="btn btn-block btn-default btn-raised"
                                        @click="createComment()"
                                    >
                                        {{ $t(`goals.update.btnSendComment`) }}
                                    </button>
                                </div>
                            </div>

                            <l-space :size="1" />

                            <div v-for="comment in comments" :key="comment._id">
                                <div style="margin-bottom: 20px">
                                    <div style="display: flex">
                                        <div
                                            style="flex-basis: auto; margin-right: 30px"
                                            class="hidden-xs"
                                        >
                                            <l-profile-picture :user="comment.sender" size="m" />
                                        </div>
                                        <div style="flex-grow: 1" class="mouseover-parent">
                                            <strong>
                                                {{ comment.sender.firstname }}
                                                {{ comment.sender.lastname }}
                                            </strong>
                                            <span v-show="comment.created_at" class="color-grey">
                                                {{ comment.created_at | moment("MMM DD, YYYY") }}
                                            </span>
                                            <span v-show="!comment.created_at">
                                                {{ $t(`goals.update.labelJustNow`) }}
                                            </span>

                                            <span
                                                v-show="comment.sender._id === user._id"
                                                class="fake-link pull-right mouseover l-default-margin-s clickable"
                                                tabindex="0"
                                                @click="removeComment(comment)"
                                                @keyup.enter="removeComment(comment)"
                                            >
                                                <i class="fal fa-trash"></i>
                                                <span class="l-default-margin-s">{{
                                                    $t(`goals.update.btnRemoveComment`)
                                                }}</span>
                                            </span>

                                            <span
                                                v-show="comment.sender._id === user._id"
                                                class="fake-link pull-right mouseover l-default-margin-s clickable"
                                                tabindex="0"
                                                @click="toggleCommentEdit(comment)"
                                                @keyup.enter="toggleCommentEdit(comment)"
                                            >
                                                <i class="fal fa-pencil"></i>
                                                <span class="l-default-margin-s">{{
                                                    $t(`goals.update.btnEditComment`)
                                                }}</span>
                                            </span>

                                            <br />
                                            <div v-if="!comment.showEdit">
                                                <div
                                                    class="mention"
                                                    @click="userClick"
                                                    v-html="comment.content"
                                                />
                                            </div>

                                            <div
                                                v-if="comment.showEdit"
                                                class="form-group label-floating"
                                                style="margin-top: 0px"
                                            >
                                                <l-rich-text-editor
                                                    v-model="comment.content"
                                                    :allow-mention="true"
                                                    :content="comment.content"
                                                    @blur="updateComment(comment)"
                                                ></l-rich-text-editor>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <!--/Comments -->

                        <!-- History -->
                        <div v-show="showInteractions === 'history'">
                            <br />

                            <div v-for="goalUpdate in goal.updates" :key="goalUpdate.created_at">
                                <div style="margin-bottom: 20px">
                                    <div style="display:flex">
                                        <div
                                            v-show="goalUpdate.actingUser.picture"
                                            style="flex-basis: auto; margin-right: 30px"
                                            class="text-center hidden-xs"
                                        >
                                            <l-profile-picture
                                                :user="goalUpdate.actingUser"
                                                size="m"
                                            />
                                        </div>
                                        <div style="flex-grow: 1">
                                            <strong>{{ goalUpdate.actingUser.fullname }}</strong>
                                            <span v-show="goalUpdate.created_at" class="color-grey">
                                                {{ goalUpdate.created_at | moment("MMM DD, YYYY") }}
                                            </span>
                                            <span v-show="!goalUpdate.created_at">
                                                {{ $t(`goals.update.labelJustNow`) }}
                                            </span>
                                            <br />
                                            {{ goalUpdate.content }}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <!--/History -->
                        <!--/Interactions -->
                    </div>
                </l-card-content>
            </div>

            <div class="col-md-4">
                <l-card-content position="right">
                    <div>
                        <!-- Contributors -->
                        <div class="material-tabs hidden-xs" style="margin-top: -20px">
                            <a>
                                <span>{{ $t(`goals.update.titleContributors`) }}</span>
                                <span class="material-tabs-bar"></span>
                            </a>
                        </div>

                        <div
                            v-for="contributor in goal.contributors"
                            :key="contributor._id"
                            class="row"
                        >
                            <div class="col-md-11 col-xs-10 user-row-md">
                                <a
                                    :href="`/app/#/profile/${contributor._id}`"
                                    :aria-label="contributor.firstname + ' ' + contributor.lastname"
                                    @click.prevent="
                                        () => {
                                            $router.push(`/profile/${contributor._id}`);
                                        }
                                    "
                                >
                                    <l-profile-picture :user="contributor" size="xm" />
                                </a>
                                <span class="l-default-margin">
                                    <span v-if="contributor.type !== 'team'">
                                        {{ contributor.firstname }} {{ contributor.lastname }}
                                    </span>
                                    <span v-else>
                                        {{ contributor.name }}
                                    </span>
                                    <br />
                                    <span class="color-grey">
                                        {{ contributor.role }}
                                    </span>
                                </span>
                            </div>
                        </div>
                        <!--/Contributors -->

                        <div v-if="goal.deadline && goal.deadline > goal.created_at">
                            <div class="material-tabs hidden-xs">
                                <a>
                                    <span>{{ $t(`goals.update.titleProgress`) }}</span>
                                    <span class="material-tabs-bar"></span>
                                </a>
                            </div>

                            <!-- Progress chart -->
                            <l-goal-progress-chart
                                :range="progressRange"
                                :data="progressData"
                            ></l-goal-progress-chart>
                            <!--/Progress chart -->
                        </div>

                        <!-- Skills -->
                        <div v-if="goal.skills && goal.skills.length">
                            <br />

                            <div class="material-tabs hidden-xs">
                                <a>
                                    <span>{{ $t(`goals.update.titleDevelopment`) }}</span>
                                    <span class="material-tabs-bar"></span>
                                </a>
                                <a
                                    v-if="user.rights.P_MATRIX && goal.user"
                                    :href="`/app/#/skill/matrix/${goal.user._id}`"
                                    _target="blank"
                                    rel="noopener noreferrer"
                                >
                                    <i class="fal fa-external-link"></i>
                                    <span class="l-default-margin-s">{{
                                        $t(`profile.feedback.btnShowSkillMatrix`)
                                    }}</span>
                                    <span class="material-tabs-bar"></span>
                                </a>
                            </div>

                            <div v-for="skill in goal.skills" :key="skill._id" class="row">
                                <div class="col-md-11 col-xs-10 user-row-md">
                                    <img
                                        :src="iconSummit"
                                        class="img-circle user-pic-sm"
                                        alt="Summit Icon"
                                    />

                                    <span class="l-default-margin">
                                        <span>
                                            {{ skill.name }}
                                        </span>
                                        <br />
                                        <span
                                            v-show="skill.type === 'localTeam'"
                                            class="color-grey"
                                        >
                                            {{ $t(`goals.update.labelTeamSkill`) }}
                                        </span>
                                        <span v-show="skill.type === 'team'" class="color-grey">
                                            {{ $t(`goals.update.labelCompanySkill`) }}
                                        </span>
                                    </span>
                                </div>
                            </div>
                        </div>
                        <!--/Skills -->

                        <!-- Learing Path -->
                        <div v-if="goal.learningPaths && goal.learningPaths.length">
                            <br />

                            <div class="material-tabs hidden-xs">
                                <a>
                                    <span>{{ $t(`goals.update.titleLearningSidebar`) }}</span>
                                    <span class="material-tabs-bar"></span>
                                </a>
                            </div>

                            <div
                                v-for="lp in goal.learningPaths.slice(0, 5)"
                                :key="lp.commonId"
                                tabindex="0"
                            >
                                <div class="row">
                                    <div class="col-md-12 user-row-md">
                                        <img
                                            :src="iconSummit"
                                            class="img-circle user-pic-sm"
                                            alt="Summit Icon"
                                        />
                                        <span class="l-default-margin">
                                            <a :href="`/app/#/learning/progress`">
                                                <span class="color-dark">
                                                    {{ lp.name }}
                                                </span>
                                            </a>
                                            <br />
                                            <a :href="`/app/#/learning/progress`"
                                                ><span class="color-grey">{{
                                                    lp.description.length > 100
                                                        ? lp.description.slice(0, 100) + "..."
                                                        : lp.description
                                                }}</span></a
                                            >
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <!-- /Learing Paths -->

                        <!-- Hiearchy -->
                        <div v-show="user.rights.G_VIEW_TREE">
                            <br />

                            <div class="material-tabs hidden-xs">
                                <a>
                                    <span>{{ $t(`goals.update.titleAlignmentSidebar`) }}</span>
                                    <span class="material-tabs-bar"></span>
                                </a>
                            </div>

                            <div
                                v-show="
                                    !(goal.parent && goal.parent._id) && goal.type !== 'goalTeam'
                                "
                                tabindex="0"
                                class="clickable"
                                @click="editGoal()"
                                @keyup.enter="editGoal()"
                            >
                                <div class="row">
                                    <div class="col-md-12 user-row-md">
                                        <img
                                            :src="iconGlobe"
                                            class="img-circle user-pic-sm"
                                            alt="Globe Icon"
                                        />
                                        <span class="l-default-margin">
                                            <span class="color-primary">
                                                {{ $t(`goals.update.labelSetParent`) }}
                                            </span>
                                            <br />
                                            <span class="color-grey">
                                                {{ $t(`goals.update.labelEditSettings`) }}
                                            </span>
                                        </span>
                                    </div>
                                </div>
                                <div style="padding-left: 20px">
                                    <i class="fal fa-caret-up"></i>
                                </div>
                            </div>

                            <div v-if="goal.parent && goal.parent._id">
                                <div class="row">
                                    <div class="col-md-12 user-row-md" style="display: flex;">
                                        <img
                                            :src="goal.parent.icon"
                                            class="img-circle user-pic-sm"
                                            alt="Parent Goal Icon"
                                        />
                                        <span class="l-default-margin tw-line-clamp-2">
                                            <a
                                                v-b-popover.hover.top="goal.parent.name"
                                                :href="`/app/#/goals/details/${goal.parent._id}`"
                                                class="fake-link"
                                                @click.prevent="
                                                    () => {
                                                        $router.push(
                                                            `/goals/details/${goal.parent._id}`,
                                                        );
                                                    }
                                                "
                                            >
                                                {{ goal.parent.name }}
                                            </a>
                                            <br />
                                            <span class="color-grey">
                                                {{
                                                    $t(`goals.update.labelParentProgress`, {
                                                        progress: goal.parent.progress,
                                                    })
                                                }}
                                            </span>
                                        </span>
                                    </div>
                                </div>
                                <div style="padding-left: 20px">
                                    <i class="fal fa-caret-up"></i>
                                </div>
                            </div>

                            <div class="user-row-md" style="display: flex;">
                                <img
                                    :src="goal.icon"
                                    class="img-circle user-pic-sm"
                                    alt="Goal Icon"
                                />
                                <span class="l-default-margin tw-line-clamp-2">
                                    <span v-b-popover.hover.top="goal.name" class="color-dark">
                                        {{ goal.name }}
                                    </span>
                                    <br />
                                    <span class="color-grey ellipsis">
                                        <strong>
                                            {{
                                                $t(`goals.update.labelThisProgress`, {
                                                    progress: goal.progress,
                                                })
                                            }}
                                        </strong>
                                    </span>
                                </span>
                            </div>

                            <div style="padding-left: 20px">
                                <i class="fal fa-caret-up"></i>
                            </div>

                            <div
                                v-for="child in goal.children.filter(Boolean)"
                                :key="child._id"
                                class="row"
                            >
                                <div class="col-md-12 user-row-md ellipse-text">
                                    <img
                                        :src="child.icon"
                                        class="img-circle user-pic-sm"
                                        alt="Child Goal Icon"
                                    />
                                    <span class="l-default-margin tw-line-clamp-2">
                                        <a
                                            v-b-popover.hover.top="child.name"
                                            :href="`/app/#/goals/details/${child._id}`"
                                            class="fake-link ellipse-text"
                                            @click.prevent="
                                                () => {
                                                    $router
                                                        .push(`/goals/details/${child._id}`)
                                                        .catch(() => {});
                                                }
                                            "
                                        >
                                            {{ child.name }}
                                        </a>
                                        <br />
                                        <span class="color-grey">
                                            {{ child.owner }}
                                            <span>{{
                                                $t(`goals.update.labelChildProgress`, {
                                                    progress: child.progress,
                                                })
                                            }}</span>
                                        </span>
                                    </span>
                                </div>
                            </div>

                            <div class="row">
                                <div
                                    class="col-md-12 user-row-md clickable"
                                    tabindex="0"
                                    @click="createChildGoal()"
                                    @keyup.enter="createChildGoal()"
                                >
                                    <img
                                        :src="iconCooperation"
                                        class="img-circle user-pic-sm"
                                        alt="Cooperation Icon"
                                    />
                                    <span class="l-default-margin">
                                        <span class="fake-link">
                                            {{ $t(`goals.update.labelCreateChild`) }}
                                        </span>
                                        <br />
                                        <span class="color-grey">
                                            {{ $t(`goals.update.labelClickHere`) }}
                                        </span>
                                    </span>
                                </div>
                            </div>
                        </div>
                        <!--/Hierarchy -->

                        <!-- Cycle -->
                        <div v-if="goal.cycle && goal.cycle.name">
                            <br />

                            <div class="material-tabs hidden-xs">
                                <a>
                                    <span>{{ $t(`goals.update.titleCycle`) }}</span>
                                    <span class="material-tabs-bar"></span>
                                </a>
                            </div>

                            <h4 class="color-grey">
                                <i class="fal fa-random"></i>
                                <a
                                    :href="`/app/#/goals/cycles/details/${goal.cycle._id}`"
                                    class="fake-link l-default-margin-s"
                                    @click.prevent="
                                        () => {
                                            $router
                                                .push(`/goals/cycles/details/${goal.cycle._id}`)
                                                .catch(() => {});
                                        }
                                    "
                                >
                                    {{ goal.cycle.name }}
                                </a>
                            </h4>
                        </div>
                        <!--/Cycle -->

                        <!-- JIRA -->
                        <div v-show="jiraEnabled && goal.jiraIssues && goal.jiraIssues.length">
                            <br />

                            <div class="material-tabs hidden-xs">
                                <a>
                                    <span>{{ $t(`goals.update.titleJiraIssues`) }}</span>
                                    <span class="material-tabs-bar"></span>
                                </a>
                            </div>

                            <div
                                v-for="issue in goal.jiraIssues"
                                :key="issue.key"
                                class="row"
                                tabindex="0"
                                @click="editGoal()"
                                @keyup.enter="editGoal()"
                            >
                                <div class="col-md-12 user-row-md" style="height: auto">
                                    <span>
                                        <a
                                            :href="`${teamJiraUrl}/browse/${issue.key}`"
                                            target="_blank"
                                            rel="noreferrer noopener"
                                        >
                                            <span class="color-primary">
                                                {{ issue.key }}
                                            </span>
                                        </a>
                                        <br />
                                        <a
                                            :href="`${teamJiraUrl}/browse/${issue.key}`"
                                            target="_blank"
                                            rel="noreferrer noopener"
                                        >
                                            <span class="color-grey">
                                                <span>
                                                    {{ issue.summaryText }}
                                                </span>
                                            </span>
                                        </a>
                                    </span>
                                </div>
                            </div>
                        </div>
                        <!--/JIRA -->
                    </div>
                </l-card-content>
            </div>
        </div>
        <l-modal
            v-if="showModal"
            size="xl"
            data-test-id="goal-edit-modal"
            :close-on-backdrop-click="false"
            @close="showDetails"
        >
            <template #header>
                {{ modalTitle }}
            </template>
            <template #body>
                <l-goals-edit :id="goal._id" />
            </template>
        </l-modal>
    </div>
</template>

<script>
import { mapGetters } from "vuex";

import debounce from "lodash.debounce";
import ObjectId from "bson-objectid";

import { alphabetically } from "@/utils/sort-object";
import format from "@/utils/format-typeahead";

import LCardContent from "@/components/LCardContent";
import LHeadline from "@/components/LHeadline";
import LInputText from "@/components/LInputText";
import LProfilePicture from "@/components/LProfilePicture";
import LProgress from "@/components/LProgress";
import LSpace from "@/components/LSpace";
import LText from "@/components/LText";
import LRichTextEditor from "@/components/LRichTextEditor";
import LGoalKREditModal from "@/components/LGoalKREditModal";
import LConfirmationModal from "@/components/LConfirmationModal";
import LGoalDetailsKeyResult from "@/components/LGoalDetailsKeyResult";
import iconCooperation from "@/img/icons/cooperation.png";
import iconGlobe from "@/img/icons/globe.png";
import iconSummit from "@/img/icons/summit.png";
import LGoalProgressChart from "@/components/LGoalProgressChart.vue";
import LModal from "@/components/LModal.vue";
import LGoalsEdit from "@/components/LGoalsEdit.vue";

export default {
    components: {
        LCardContent,
        LHeadline,
        LInputText,
        LProfilePicture,
        LProgress,
        LSpace,
        LText,
        LRichTextEditor,
        LConfirmationModal,
        LGoalDetailsKeyResult,
        LGoalKREditModal,
        LGoalProgressChart,
        LModal,
        LGoalsEdit,
    },
    props: {
        id: {
            type: String,
            required: true,
        },
    },
    data() {
        return {
            autoSaving: false,

            freezeGoalCheck: false,
            unfreezeGoalCheck: false,
            removeGoalCheck: false,

            error: null,

            goal: {},

            showInteractions: "feedback",
            feedbackRequestSuccess: false,
            feedbackRequestUsers: [],
            feedbackRequestQuestion: "",
            comment: "",
            comments: [],
        };
    },
    computed: {
        ...mapGetters({
            user: "users/current/user",
            getGoalDetails: "goals/goals/details",
            getGoalComments: "goals/comments/list",
            usersSearchList: "users/search/list",
            getUserList: "users/list/list",
            jiraStatus: "jira/status",
            eventNames: "events/names",
            eventProperties: "events/properties",
        }),
        tabs() {
            return [
                {
                    label: this.$t(`goals.update.btnGoBack`),
                    icon: "fal fa-caret-left",
                    iconBeforeLabel: true,
                    show: true,
                    testId: "goals-details-back",
                    handler: this.goBack,
                },
                {
                    label: this.$t(`goals.update.labelAutoSaving`),
                    icon: "fal fa-sync fa-spin",
                    iconBeforeLabel: true,
                    show: this.autoSaving,
                    tabClass: "descriptive",
                    testId: "goals-details-auto-saving",
                },
                {
                    label: this.$t(`goals.update.labelFrozen`),
                    icon: "fal fa-lock",
                    iconBeforeLabel: true,
                    show: this.goal.status === "archived" && !this.autoSaving,
                    popover: this.$t(`goals.update.helpFrozen`),
                    tabClass: "descriptive",
                    testId: "goals-details-archived",
                },
                {
                    label: this.$t(`goals.update.btnRemove`),
                    show: this.goal.access.canDelete,
                    handler: () => {
                        this.removeGoalCheck = true;
                    },
                    testId: "goals-details-remove",
                },
                {
                    label: this.$t(`goals.update.btnUnfreezeProgress`),
                    show: this.goal.access.canArchive && this.goal.status === "archived",
                    handler: () => {
                        this.unfreezeGoalCheck = true;
                    },
                    testId: "goals-details-unfreeze",
                },
                {
                    label: this.$t(`goals.update.btnFreezeProgress`),
                    show: this.goal.access.canArchive && this.goal.status === "live",
                    handler: () => {
                        this.freezeGoalCheck = true;
                    },
                    testId: "goals-details-freeze",
                },
                {
                    label: this.$t(`goals.update.btnCopy`),
                    show: this.goal.access.canUpdateGoal,
                    handler: () => this.copyGoal(),
                    testId: "goals-details-copy",
                },
                {
                    label: this.$t(`goals.update.labelDraft`),
                    handler: () => this.editGoal(),
                    show: this.goal.status === "draft",
                    tabClass: "highlight",
                    testId: "goals-details-edit-draft",
                },
                {
                    label:
                        this.user._id === this.goal.managedBy._id
                            ? this.$t(`goals.update.labelRequiresApproval`)
                            : this.$t(`goals.update.labelApprovalPending`),
                    show: this.goal.status === "approvalPending",
                    testId: "goals-details-approval-pending",
                },
                {
                    label: this.$t(`goals.update.btnApprove`),
                    handler: () => this.approveGoal(),
                    show:
                        this.goal.status == "approvalPending" &&
                        this.goal.managedBy._id == this.user._id,
                    testId: "goals-details-approve",
                },
                {
                    tabClass: "space",
                    show: true,
                },
                {
                    label: this.$t(`goals.update.btnEdit`),
                    handler: () => this.editGoal(),
                    show: this.goal.access.canUpdateGoal,
                    tabClass: "btn-default",
                    testId: "goals-details-edit",
                    isButton: true,
                },
            ];
        },
        users() {
            let list = this.usersSearchList
                // Filter out current user
                .filter(user => user._id !== this.user._id)
                // Filter out selected users
                .filter(user => !this.feedbackRequestUsers.map(u => u._id).includes(user._id));

            if (!list.length) {
                list = this.getUserList({ key: "directTeam" })
                    // Filter out selected users
                    .filter(user => !this.feedbackRequestUsers.map(u => u._id).includes(user._id));
            }

            return list.map(format.users);
        },
        teamJiraUrl() {
            return this.jiraStatus?.teamUrl;
        },
        jiraEnabled() {
            return Boolean(this.jiraStatus?.enabled && this.jiraStatus?.accountId);
        },
        filteredGoalTags() {
            return this.goal?.tags?.filter?.(tag => tag?.name) ?? [];
        },
        keyResults() {
            let keyResults = this.goal?.keyResults || [];

            return keyResults.sort(alphabetically("type", "desc"));
        },
        progressRange() {
            return { end: this.goal.deadline, start: this.goal.created_at };
        },
        progressData() {
            const validUpdates = this.goal.updates.filter(
                update =>
                    (update.change && update.updateType === "total") ||
                    update.updateType === "created",
            );
            let results = {};
            let resultsArray = [];

            // only use the latest value of a given day to avoid jumpy charts

            for (let i = 0; i < validUpdates.length; i++) {
                let date = this.$moment(validUpdates[i].updated_at).format("MM-DD-YYYY");
                let value = validUpdates[i].newValue || 0;
                if (!results[date]) {
                    results[date] = value;
                }
            }

            for (const [date, value] of Object.entries(results)) {
                resultsArray.push({
                    t: this.$moment(date, "MM-DD-YYYY")
                        .toDate()
                        .toISOString(),
                    y: value,
                });
            }
            return resultsArray;
        },
    },
    watch: {
        id: {
            immediate: true,
            handler() {
                this.goal = this.getGoalDetails(this.id);
                this.comments = this.getGoalComments(this.id);

                this.$store.dispatch({
                    type: "events/track",
                    payload: {
                        name: this.eventNames.GOAL_VIEWED,
                        properties: {
                            [this.eventProperties.GOAL_ID]: this.goal._id,
                            [this.eventProperties.GOAL_CYCLE_ID]: this.goal.cycle?._id,
                        },
                    },
                });
            },
        },
        ["goal.metricCurrent"]: {
            handler: debounce(function handler(metricCurrent) {
                this.updateGoalMetricCurrent(metricCurrent);
            }, 1000),
        },
        $route: {
            immediate: true,
            deep: true,
            handler(route) {
                this.goal = this.getGoalDetails(this.id);
                this.showModal = route.meta && route.meta.modal;
                this.modalTitle = this.goal.isNew
                    ? this.$t("goals.update.titleNewGoal")
                    : this.$t("goals.update.titleEditGoal");
            },
        },
    },
    created() {
        this.$emit("section", "goal"); // This is a bit hacky ...

        this.iconCooperation = iconCooperation;
        this.iconGlobe = iconGlobe;
        this.iconSummit = iconSummit;
    },
    beforeRouteEnter(_, from, next) {
        // We are saving the previous path to allow for the "back" button to be a bit smarter about
        // where to go back to (e.g. if the user came to the details from the feed, then we should
        // redirect back to the feed).
        next(vm => {
            if (from.fullPath !== "/") {
                vm.previousRoute = from.fullPath;
            }
        });
    },
    methods: {
        async goalPartialUpdate() {
            this.autoSaving = true;

            await this.$store.dispatch({
                type: "goals/goals/update",
                payload: {
                    goal: {
                        ...this.goal,
                        scope: "partial",
                    },
                },
            });

            this.goal = this.getGoalDetails(this.id);

            setTimeout(() => {
                this.autoSaving = false;
            }, 500);
        },
        keyResultUpdated() {
            this.goal = this.getGoalDetails(this.id);
            this.comments = this.getGoalComments(this.id);
        },

        userClick(e) {
            const userId = e.target?.getAttribute("data-mention-id");
            if (userId) {
                this.$router.push(`/profile/${userId}`).catch(() => {});
            }
        },
        async approveGoal() {
            this.autoSaving = true;

            await this.$store.dispatch({
                type: "goals/goals/update",
                payload: {
                    goal: {
                        ...this.goal,
                        status: "live",
                        scope: "full",
                    },
                },
            });

            this.goal = this.getGoalDetails(this.id);

            this.autoSaving = false;
        },
        copyGoal() {
            const copiedGoal = { ...this.goal };

            copiedGoal.status = "draft";
            copiedGoal.metricCurrent = "0";
            copiedGoal.name += " (Copy)";
            copiedGoal.keyResults = this.goal.keyResults.map(keyResult => ({
                ...keyResult,
                _id: ObjectId().toString(),
                progress: 0,
                metricCurrent: "0",
            }));

            this.$emit("create", copiedGoal);
        },
        async freezeGoal() {
            this.autoSaving = true;

            await this.$store.dispatch({
                type: "goals/goals/update",
                payload: {
                    goal: {
                        ...this.goal,
                        status: "archived",
                        scope: "status",
                    },
                },
            });

            this.goal = this.getGoalDetails(this.id);

            this.autoSaving = false;
            this.freezeGoalCheck = false;
        },
        async unfreezeGoal() {
            this.autoSaving = true;

            await this.$store.dispatch({
                type: "goals/goals/update",
                payload: {
                    goal: {
                        ...this.goal,
                        status: "live",
                        scope: "status",
                    },
                },
            });

            this.goal = this.getGoalDetails(this.id);

            this.autoSaving = false;
            this.unfreezeGoalCheck = false;
        },
        async removeGoal() {
            this.autoSaving = true;

            await this.$store.dispatch({
                type: "goals/goals/update",
                payload: {
                    goal: {
                        ...this.goal,
                        status: "removed",
                        scope: "status",
                    },
                },
            });

            this.autoSaving = false;
            this.removeGoalCheck = false;

            this.$router.push(`/goals`);
        },

        /* Status */
        async updateGoalStatus(status) {
            if (this.goal.status === "archived") {
                return;
            }

            this.goal.toDiscuss = this.goal.toDiscuss === status ? "zero" : status;

            await this.goalPartialUpdate();
        },

        /* Metric */
        async updateGoalMetricCurrent(metricCurrent) {
            if (this.goal.status === "archived") {
                return;
            }

            this.autoSaving = true;

            await this.$store.dispatch({
                type: "goals/goals/update",
                payload: {
                    goal: {
                        ...this.goal,
                        metricCurrent,
                        scope: "partial",
                    },
                },
            });

            this.goal = this.getGoalDetails(this.id);

            this.autoSaving = false;
        },

        /* Key Results */
        async updateKR(keyResult) {
            if (this.goal.status === "archived") {
                return;
            }

            const index = this.goal.keyResults.findIndex(kr => kr._id === keyResult._id);
            this.goal.keyResults[index] = keyResult;

            await this.goalPartialUpdate();
        },

        /* Feedback Request */
        typeaheadUserOnSelect(user) {
            this.feedbackRequestUsers.push(user);
        },
        typeaheadUserOnInputChange(query) {
            if (query.length >= 2) {
                this.$store.dispatch("users/search/findByName", { query });
            } else {
                this.$store.dispatch("users/search/reset");
            }
        },
        removeSelectedUser({ user }) {
            const index = this.feedbackRequestUsers.findIndex(u => u._id === user._id);

            if (index > -1) {
                this.feedbackRequestUsers.splice(index, 1);
            }
        },

        /* Comments */
        async createComment() {
            if (!this.comment) {
                return;
            }

            await this.$store.dispatch({
                type: "goals/comments/create",
                payload: { commentContent: this.comment, goalId: this.goal._id },
            });

            this.comment = "";
            this.comments = this.getGoalComments(this.id);
        },
        async removeComment(comment) {
            await this.$store.dispatch({
                type: "goals/comments/delete",
                payload: { commentId: comment._id, goalId: this.goal._id },
            });

            this.comments = this.getGoalComments(this.id);
        },
        async updateComment(comment) {
            await this.$store.dispatch({
                type: "goals/comments/update",
                payload: {
                    commentId: comment._id,
                    commentContent: comment.content,
                    goalId: this.goal._id,
                },
            });

            this.comments = this.getGoalComments(this.id);
        },
        toggleCommentEdit(comment) {
            this.$set(comment, "showEdit", !comment.showEdit);
        },
        editGoal() {
            this.$router.replace(`/goals/edit/${this.goal._id}`).catch(() => {});
        },
        createChildGoal() {
            const childGoal = {
                parent: {
                    _id: this.goal._id,
                    name: this.goal.name,
                },
            };

            this.$emit("create", childGoal);
        },
        goBack() {
            this.$router.go(-1);
        },
        showDetails() {
            if (!this.goal.isNew) {
                this.$router.replace(`/goals/details/${this.id}`).catch(() => {});
            } else {
                this.$router.go(-1);
            }
        },
    },
};
</script>

<style scoped>
.goals-details >>> .b-dropdown button.dropdown-toggle {
    padding: 0;
}
.goals-details >>> .b-dropdown button.dropdown-toggle .dropdown-toggle {
    padding: 5px 0;
    padding-left: 1rem;
}
.sidebar-row {
    max-height: 80px;
    overflow: hidden;
}
</style>
